<template>
  <div class="home">
    <!-- 加载态 -->
    <div class="loading-wrap" v-if="loading">
      <div class="spinner"></div>
      <p>正在加载首页数据…</p>
    </div>

    <template v-else>
      <!-- 顶部轮播图（info/banner/list） -->
      <div class="container">
        <div class="carousel" v-if="swiperList.length">
          <div
            class="carousel-track"
            :style="{ transform: `translateX(-${current * 100}%)` }"
          >
            <div
              v-for="item in swiperList"
              :key="item.bannerId"
              class="carousel-slide"
              :style="item.icon ? { backgroundImage: `url(${item.icon})` } : { background: '#2444cb' }"
              @click="onBannerClick(item)"
            ></div>
          </div>
          <button class="carousel-arrow prev" @click="prev">‹</button>
          <button class="carousel-arrow next" @click="next">›</button>
          <div class="carousel-dots">
            <span
              v-for="(item, i) in swiperList"
              :key="item.bannerId"
              class="dot"
              :class="{ active: i === current }"
              @click="goTo(i)"
            ></span>
          </div>
        </div>
        <div class="empty-banner" v-else>暂无轮播图</div>
      </div>

      <!-- 出海测算 / AI 财税 功能入口（一行一个，左右对称） -->
      <section class="section">
        <div class="container">
          <div class="feature-grid">
            <div
              v-for="(entry, idx) in entryList"
              :key="entry.id"
              class="feature-block"
              :class="{ reverse: idx % 2 === 1 }"
            >
              <div class="feature-media">
                <img :src="entry.icon" :alt="entry.name" @error="onImgError" />
              </div>
              <div class="feature-info">
                <h3>{{ entry.name }}</h3>
                <p>{{ entry.desc }}</p>
                <div class="feature-action">
                  <button class="btn-primary" @click="onEntryClick(entry)">{{ entry.btn }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 商务活动（info/business/list，展示进行中） -->
      <section class="section">
        <div class="container">
          <div class="section-head">
            <div class="section-title">商务活动</div>
            <span class="section-more" v-if="businessList.length" @click="$router.push('/business')">查看全部 ›</span>
          </div>
          <div class="card-grid home-hscroll" v-if="businessList.length">
            <div
              v-for="item in sliceHome(businessList)"
              :key="item.id"
              class="card"
              @click="openBusiness(item)"
            >
              <div class="card-cover business-cover">
                <img v-if="item.cover" :src="item.cover" :alt="item.title" @error="onImgError" />
                <span v-else class="cover-letter">活动</span>
                <span class="biz-state" :class="String(item.status) === '0' ? 'ongoing' : 'ended'">
                  {{ String(item.status) === '0' ? '进行中' : '已结束' }}
                </span>
              </div>
              <div class="card-body">
                <div class="card-title">{{ item.title }}</div>
                <div class="card-desc" v-if="item.startDate">活动日期：{{ item.startDate }} ~ {{ item.endDate }}</div>
                <div class="card-detail-btn">查看详情 ›</div>
              </div>
            </div>
          </div>
          <div class="empty-block" v-else>暂无活动</div>
        </div>
      </section>

      <!-- 精选产品（product/list） -->
      <section class="section section-alt">
        <div class="container">
          <div class="section-head">
            <div class="section-title">精选产品</div>
            <span class="section-more" v-if="productList.length" @click="$router.push('/products')">查看全部 ›</span>
          </div>
          <div class="card-grid home-hscroll" v-if="productList.length">
            <div
              v-for="item in productList"
              :key="item.id"
              class="card"
              @click="$router.push('/product/' + item.id)"
            >
              <div class="card-cover product-cover">
                <span class="cover-tag" v-if="item.typeName">{{ item.typeName }}</span>
                <img v-if="item.cover" :src="item.cover" :alt="item.name" @error="onImgError" />
                <span v-else class="cover-letter">{{ item.name.slice(0, 1) }}</span>
              </div>
              <div class="card-body">
                <div class="card-title">{{ item.name }}</div>
                <div class="card-desc">{{ item.summary }}</div>
                <div class="card-source" v-if="item.sourcePlace">来源地：{{ item.sourcePlace }}</div>
                <div class="card-detail-btn">查看详情</div>
              </div>
            </div>
          </div>
          <div class="empty-block" v-else>暂无产品</div>
        </div>
      </section>

      <!-- 出海案例（anli.json） -->
      <section class="section">
        <div class="container">
          <div class="section-head">
            <div class="section-title">出海案例</div>
            <span class="section-more" v-if="successCaseList.length" @click="$router.push('/cases')">查看全部 ›</span>
          </div>
          <div class="card-grid home-hscroll" v-if="successCaseList.length">
            <div
              v-for="item in sliceHome(successCaseList)"
              :key="item.id"
              class="card case-card"
              @click="$router.push('/case/' + item.id)"
            >
              <div class="case-header" :style="{ background: item.headerBg || '#2444cb' }">
                <span class="case-tag">{{ item.tagText }}</span>
                <span class="case-title">{{ item.title }}</span>
              </div>
              <div class="card-body">
                <div class="card-desc">{{ item.content }}</div>
                <div class="card-detail-btn case-btn" :style="{ background: item.headerBg || '#2444cb' }">查看详情</div>
              </div>
            </div>
          </div>
          <div class="empty-block" v-else>暂无案例</div>
        </div>
      </section>

      <!-- 行业专家（info/expert/list） -->
      <section class="section section-alt">
        <div class="container">
          <div class="section-head">
            <div class="section-title">行业专家</div>
            <span class="section-more" v-if="expertList.length" @click="$router.push('/experts')">查看全部 ›</span>
          </div>
          <div class="h-scroll" v-if="expertList.length">
            <div
              v-for="item in expertList"
              :key="item.id"
              class="card expert-card-item"
              @click="$router.push('/expert/' + item.id)"
            >
              <div class="expert-cover">
                <img v-if="item.cover" :src="item.cover" :alt="item.name" @error="onImgError" />
                <span v-else>{{ item.name.slice(0, 1) }}</span>
              </div>
              <div class="card-body">
                <div class="card-title">{{ item.name }}</div>
                <div class="card-desc">{{ item.tags }}</div>
                <div class="card-detail-btn">查看详情</div>
              </div>
            </div>
          </div>
          <div class="empty-block" v-else>暂无专家</div>
        </div>
      </section>

      <!-- 政经动态（info/dynamic/list） -->
      <section class="section">
        <div class="container">
          <div class="section-head">
            <div class="section-title">政经动态</div>
            <span class="section-more" v-if="dynamicList.length" @click="$router.push('/dynamics')">查看全部 ›</span>
          </div>
          <div class="card-grid home-hscroll" v-if="dynamicList.length">
            <div
              v-for="item in sliceHome(dynamicList)"
              :key="item.id"
              class="card"
              @click="openDynamic(item)"
            >
              <div class="card-cover dynamic-cover">
                <img v-if="item.cover" :src="item.cover" :alt="item.name" @error="onImgError" />
                <span v-else class="cover-letter">政经</span>
              </div>
              <div class="card-body">
                <div class="card-title">{{ item.name }}</div>
                <div class="card-desc">{{ item.introduction }}</div>
                <div class="card-detail-btn">阅读原文 ›</div>
              </div>
            </div>
          </div>
          <div class="empty-block" v-else>暂无动态</div>
        </div>
      </section>

      <!-- 投资资讯（info/investment/list） -->
      <section class="section">
        <div class="container">
          <div class="section-head">
            <div class="section-title">投资资讯</div>
            <span class="section-more" v-if="investmentList.length" @click="$router.push('/investments')">查看全部 ›</span>
          </div>
          <div class="card-grid home-hscroll" v-if="investmentList.length">
            <div
              v-for="item in sliceHome(investmentList)"
              :key="item.id"
              class="card"
              @click="openInvestment(item)"
            >
              <div class="card-cover investment-cover">
                <img v-if="item.cover" :src="item.cover" :alt="item.name" @error="onImgError" />
                <span v-else class="cover-letter">投资</span>
              </div>
              <div class="card-body">
                <div class="card-title">{{ item.name }}</div>
                <div class="card-desc">{{ item.introduction }}</div>
                <div class="card-detail-btn">阅读原文 ›</div>
              </div>
            </div>
          </div>
          <div class="empty-block" v-else>暂无资讯</div>
        </div>
      </section>

      <!-- 合作伙伴（zjs/mini/friendLink/list） -->
      <section class="section">
        <div class="container">
          <div class="section-head">
            <div class="section-title">合作伙伴</div>
            <span class="section-more" v-if="partnerList.length" @click="$router.push('/partners')">更多 ›</span>
          </div>
          <div class="partner-grid" v-if="partnerList.length">
            <div class="partner-item" v-for="p in partnerList.slice(0, 6)" :key="p.id">
              <img v-if="p.icon" :src="p.icon" :alt="p.title" @error="onImgError" />
              <span v-else class="partner-ph">{{ p.title.slice(0, 1) }}</span>
              <span class="partner-name">{{ p.title }}</span>
            </div>
          </div>
          <div class="empty-block" v-else>暂无合作伙伴</div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { entryList } from '../data/modules'
import {
  fetchBanners,
  fetchProducts,
  fetchExperts,
  fetchDynamics,
  fetchOverseasCases,
  fetchInvestment,
  fetchBusiness,
  fetchFriendLinks,
  openAiFinance
} from '../api/home'

export default {
  name: 'Home',
  data () {
    return {
      entryList,
      loading: true,
      swiperList: [],
      productList: [],
      successCaseList: [],
      expertList: [],
      dynamicList: [],
      investmentList: [],
      businessList: [],
      partnerList: [],
      current: 0,
      timer: null,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200
    }
  },
  created () {
    this.loadAll()
  },
  mounted () {
    this.startAuto()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy () {
    this.stopAuto()
    window.removeEventListener('resize', this.onResize)
  },
  computed: {
    isMobile () {
      return this.windowWidth <= 768
    },
    // 与 .card-grid (auto-fill, minmax 260) 一致的每行列数，保证只渲染一行
    rowCount () {
      const inner = Math.min(this.windowWidth, 1200) - 40 // 容器 padding 20*2
      const gap = 22
      const min = 260
      return Math.max(1, Math.floor((inner + gap) / (min + gap)))
    }
  },
  methods: {
    // 移动端（横向滑动）展示全部，桌面端按 rowCount 只显示一行
    sliceHome (list) {
      return this.isMobile ? list : list.slice(0, this.rowCount)
    },
    async loadAll () {
      try {
        const [banners, products, experts, dynamics, cases, investments, businesses, partners] = await Promise.all([
          fetchBanners(),
          fetchProducts(),
          fetchExperts(),
          fetchDynamics(),
          fetchOverseasCases({ pageSize: 12 }),
          fetchInvestment(),
          fetchBusiness(),
          fetchFriendLinks({ pageSize: 50, status: '0' })
        ])
        this.swiperList = banners
        this.productList = products
        this.expertList = experts
        this.dynamicList = dynamics
        this.successCaseList = cases.rows || []
        this.investmentList = investments
        this.businessList = businesses
        this.partnerList = partners.rows || []
      } catch (e) {
        this.$toast.show({ message: (e && e.message) || '数据加载失败', type: 'error' })
      } finally {
        this.loading = false
      }
    },
    onBannerClick (item) {
      // 仅 http(s) 链接直接新窗口打开，其他类型不做任何处理
      if (item && item.url && item.url.indexOf('http') === 0) {
        window.open(item.url, '_blank')
      }
    },
    // 出海测算/AI 财税 功能入口：按 entry.type 分流
    onEntryClick (entry) {
      if (entry.type === 'link' && entry.url) {
        window.open(entry.url, '_blank')
      } else if (entry.type === 'token') {
        openAiFinance()
      } else if (entry.type === 'route' && entry.path) {
        this.$router.push(entry.path)
      }
    },
    onResize () {
      this.windowWidth = window.innerWidth
    },
    openDynamic (item) {
      if (item && item.link) {
        window.open(item.link, '_blank')
      } else {
        this.$toast.show({ message: '该动态暂无可访问原文', type: 'error' })
      }
    },
    openInvestment (item) {
      if (item && item.link) {
        window.open(item.link, '_blank')
      } else {
        this.$toast.show({ message: '该资讯暂无可访问原文', type: 'error' })
      }
    },
    openBusiness (item) {
      if (item && item.url) {
        window.open(item.url, '_blank')
      } else {
        this.$toast.show({ message: '该活动暂无可访问链接', type: 'error' })
      }
    },
    onImgError (e) {
      // 图片加载失败时隐藏，露出卡片底色
      e.target.style.display = 'none'
    },
    startAuto () {
      this.timer = setInterval(() => this.next(), 4000)
    },
    stopAuto () {
      if (this.timer) clearInterval(this.timer)
    },
    next () {
      if (!this.swiperList.length) return
      this.current = (this.current + 1) % this.swiperList.length
    },
    prev () {
      if (!this.swiperList.length) return
      this.current = (this.current - 1 + this.swiperList.length) % this.swiperList.length
    },
    goTo (i) {
      this.current = i
    }
  }
}
</script>

<style scoped>
.feature-grid {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.feature-block {
  display: flex;
  align-items: stretch;
  gap: 40px;
  background: #fff;
  border: 1px solid #e6ebf5;
  border-radius: 16px;
  padding: 22px 28px;
  box-shadow: 0 6px 22px rgba(36, 68, 203, 0.08);
}
.feature-block.reverse {
  flex-direction: row-reverse;
}
.feature-media {
  flex: 1.35;
  min-width: 0;
  height: 320px;
  border-radius: 12px;
  overflow: hidden;
  background: #eef2fb;
}
.feature-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.feature-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.feature-info h3 {
  color: #2444cb;
  font-size: 22px;
  margin: 0 0 12px;
}
.feature-info p {
  color: #5b6b8c;
  font-size: 15px;
  line-height: 1.8;
  margin: 0;
}
.feature-action {
  margin-top: 20px;
}
.section-alt {
  background: #fff;
}

/* 轮播图 */
.carousel-slide {
  background-size: cover;
  background-position: center;
  position: relative;
}
.empty-banner {
  height: 200px;
  background: #eef2fb;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa6c0;
}

/* 专家封面 */
.expert-cover {
  height: 200px;
  background: #f4f7fe;
  color: var(--primary);
  font-size: 46px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.expert-cover img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.dynamic-cover {
  background: linear-gradient(135deg, #6f9bff, #2444cb);
  position: relative;
}
.dynamic-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.investment-cover {
  background: linear-gradient(135deg, #38b6a4, #1f7a8c);
  position: relative;
}
.investment-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.business-cover {
  background: linear-gradient(135deg, #f0a04b, #d9772e);
  position: relative;
}
.business-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.biz-state {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
}
.biz-state.ongoing {
  background: #1f9d55;
}
.biz-state.ended {
  background: rgba(51, 65, 92, 0.78);
}
.cover-letter {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
}

/* 卡片封面：图片优先 */
.card-cover {
  height: 160px;
  background: linear-gradient(135deg, #6f9bff, #2444cb);
  position: relative;
  overflow: hidden;
}
.card-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 精选产品封面：等比伸缩容器，图片完整展示不裁切 */
.product-cover {
  height: auto;
  aspect-ratio: 12 / 5;
  background: #f5f7fb;
}
.product-cover img {
  object-fit: contain;
}

.loading-wrap {
  text-align: center;
  padding: 120px 0;
  color: #5b6b8c;
}
.spinner {
  width: 42px;
  height: 42px;
  margin: 0 auto 16px;
  border: 4px solid #e6ebf5;
  border-top-color: #2444cb;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.empty-block {
  padding: 40px 0;
  text-align: center;
  color: #9aa6c0;
  background: #f6f8fd;
  border-radius: 12px;
}

/* 合作伙伴 */
.partner-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 20px;
}
.partner-item {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.partner-item:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-hover);
}
.partner-item img {
  width: 100%;
  height: 120px;
  object-fit: contain;
  object-position: center;
  padding: 14px;
  box-sizing: border-box;
  display: block;
  background: #fff;
}
.partner-ph {
  width: 100%;
  height: 110px;
  background: var(--primary);
  color: #fff;
  font-size: 36px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}
.partner-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-main);
  text-align: center;
  padding: 14px 12px 16px;
}

@media (max-width: 768px) {
  .feature-block,
  .feature-block.reverse {
    flex-direction: column;
    text-align: center;
    padding: 22px;
    gap: 18px;
  }
  .feature-media {
    flex: none;
    width: 100%;
    height: 200px;
  }
  .feature-action {
    display: flex;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  /* 首页多模块移动端横向滑动，与行业专家一致（卡片尺寸同 PC） */
  .home-hscroll {
    display: flex;
    gap: 18px;
    overflow-x: auto;
    padding-bottom: 10px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .home-hscroll::-webkit-scrollbar {
    display: none;
  }
  .home-hscroll > .card {
    min-width: 280px;
    width: 280px;
    flex-shrink: 0;
  }

  /* 行业专家卡片同步固定宽度，防止长内容撑宽导致横滑效果与 5 个模块不一致 */
  .h-scroll > .expert-card-item {
    width: 280px;
  }
}
</style>
